import { cloneDeep } from 'lodash/fp';

import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { SelectedAppliances } from './../../../core/types/appliances';
import { CookingAppliancesState } from './cookingAppliance.types';

import { SubsectionButtonOption } from '~/core/enums';
import { ApplianceModel } from '~/core/models';
import {
  ApplianceId,
  ApplianceQtyType,
  SelectedApplianceProductType,
} from '~/core/types/appliances';
import { decById, incById } from '~/core/utils';

const initialState: CookingAppliancesState = {
  selectedCooktopApplianceProduct: { selected: {} },
  selectedRangeApplianceProduct: { selected: {} },
  suppliedCookingAppliances: [],
  browsedCookingAppliances: [],
  selectedBrowsingOption: SubsectionButtonOption.NONE,
  selectedCookingApplianceId: undefined,
};

const cookingApplianceSlice = createSlice({
  name: 'cookingAppliance',
  initialState,
  reducers: {
    resetSelectedBrowsingOptionAndCookingApplianceId: (state) => {
      state.selectedBrowsingOption = initialState.selectedBrowsingOption;
      state.selectedCookingApplianceId =
        initialState.selectedCookingApplianceId;
    },
    updateSuppliedCookingAppliances: (
      state,
      action: PayloadAction<ApplianceModel[]>
    ) => {
      state.suppliedCookingAppliances = action.payload;
    },
    updateBrowsedCookingAppliances: (
      state,
      action: PayloadAction<ApplianceModel[]>
    ) => {
      state.browsedCookingAppliances = action.payload;
    },
    resetBrowsedCookingAppliances: (state) => {
      state.browsedCookingAppliances = initialState.browsedCookingAppliances;
    },
    updateSelectedBrowsingOption: (
      state,
      action: PayloadAction<SubsectionButtonOption>
    ) => {
      state.selectedBrowsingOption = action.payload;
    },
    resetSelectedCookingApplianceOption: (state) => {
      state.selectedBrowsingOption = initialState.selectedBrowsingOption;
    },
    updateSelectedCookingApplianceId: (
      state,
      action: PayloadAction<number>
    ) => {
      state.selectedCookingApplianceId = action.payload;
    },
    resetSelectedCookingApplianceId: (state) => {
      state.selectedCookingApplianceId =
        initialState.selectedCookingApplianceId;
    },
    increaseCookingAppliance: (
      state,
      {
        payload: { selectedType, applianceId, qtyType },
      }: PayloadAction<{
        selectedType: SelectedApplianceProductType;
        applianceId: ApplianceId;
        qtyType: ApplianceQtyType;
      }>
    ) => {
      const currentState = cloneDeep(state[selectedType]);
      const newApplianceQty = incById(
        state[selectedType].selected[applianceId],
        qtyType
      );

      state[selectedType].selected[applianceId] = newApplianceQty;
      state[selectedType].previousSelected = currentState;
    },
    decreaseCookingAppliance: (
      state,
      {
        payload: { selectedType, applianceId, qtyType },
      }: PayloadAction<{
        selectedType: SelectedApplianceProductType;
        applianceId: ApplianceId;
        qtyType: ApplianceQtyType;
      }>
    ) => {
      const currentState = cloneDeep(state[selectedType]);
      const newApplianceQty = decById(
        state[selectedType].selected[applianceId],
        qtyType
      );

      state[selectedType].selected[applianceId] = newApplianceQty;
      state[selectedType].previousSelected = currentState;
    },
    setCookingAppliance: (
      state,
      {
        payload: { selectedType, selectedAppliances },
      }: PayloadAction<{
        selectedType: SelectedApplianceProductType;
        selectedAppliances: SelectedAppliances | undefined;
      }>
    ) => {
      state[selectedType] = selectedAppliances ?? initialState[selectedType];
    },
    syncCookingApplianceOnShape: (
      state,
      {
        payload: { selectedType, applianceId, count, resetOnCard },
      }: PayloadAction<{
        selectedType: SelectedApplianceProductType;
        applianceId: ApplianceId;
        count: number;
        resetOnCard?: boolean;
      }>
    ) => {
      const currentState = cloneDeep(state[selectedType]);

      if (!state[selectedType].selected[applianceId]) {
        state[selectedType].selected[applianceId] = { onCard: 0, onShape: 0 };
      }

      state[selectedType].selected[applianceId].onShape = count;

      if (resetOnCard && count === 0) {
        state[selectedType].selected[applianceId].onCard = 0;
      }

      state[selectedType].previousSelected = currentState;
    },
    resetSelectedCookingAndRanges: (state) => {
      state.selectedCooktopApplianceProduct =
        initialState.selectedCooktopApplianceProduct;
      state.selectedRangeApplianceProduct =
        initialState.selectedRangeApplianceProduct;
    },
    resetCookingApplianceById: (
      state,
      {
        payload: { selectedType, applianceIdToRemove },
      }: PayloadAction<{
        selectedType: SelectedApplianceProductType;
        applianceIdToRemove: ApplianceId;
      }>
    ) => {
      const currentState = cloneDeep(state[selectedType]);

      const updatedSelectedState = { ...state[selectedType].selected };

      delete updatedSelectedState[applianceIdToRemove];

      state[selectedType].selected = updatedSelectedState;
      state[selectedType].previousSelected = currentState;
    },
  },
});

export const {
  decreaseCookingAppliance,
  increaseCookingAppliance,
  resetBrowsedCookingAppliances,
  resetSelectedBrowsingOptionAndCookingApplianceId,
  resetCookingApplianceById,
  resetSelectedCookingAndRanges,
  resetSelectedCookingApplianceId,
  resetSelectedCookingApplianceOption,
  setCookingAppliance,
  syncCookingApplianceOnShape,
  updateBrowsedCookingAppliances,
  updateSelectedBrowsingOption,
  updateSelectedCookingApplianceId,
  updateSuppliedCookingAppliances,
} = cookingApplianceSlice.actions;

export default cookingApplianceSlice.reducer;
